<template>
  <section class="ProfessionPageSectionPeopleSwiper bg-zinc-50">
    <div class="contentHeader">
      <div class="cityButtons">
        <div
          v-for="(city, index) in firstFewCitiesWithPersonCount"
          :key="city.seoName"
          class="cityButton"
          :class="{
            active: index === selectedCityIndex,
          }"
          @click="changeCity(city)"
        >
          {{ city.name }}
        </div>
      </div>
      <div class="search">
        <AutocompleteCity
          :value="selectedCity"
          :placeholder="$t('citySelector.cities.placeholder')"
          @input="changeCity($event)"
        />
      </div>
    </div>

    <div class="contentBody">
      <img
        v-if="loading"
        v-lazy-load data-src="~/assets/img/loader.svg"
        width="100"
        class="peopleListLoader"
      />
      <PeopleSwiper
        v-if="peopleByCity?.length > 0"
        :class="{ loadingLook: loading }"
        class="mb-2.5"
      >
        <CardPerson
          v-for="person in peopleByCity"
          :key="person.id"
          class="min-w-[540px]"
          :highlighted-profession="profession"
          :highlighted-city="selectedCity"
          :person="person"
        />
      </PeopleSwiper>

      <NuxtLink
        v-if="peopleByCity?.length > 0"
        :to="
          localePath({
            name: 'professionals',
            params: {
              profession: profession.seoName,
              city: selectedCity.seoName,
            },
          })
        "
        class="inline-block w-full md:w-auto p-2 rounded-[4px] border-transparent text-white hover:text-white bg-primary hover:bg-primary-dark mt-2 whitespace-normal"
      >
        {{
          $t("professionalsPage.showAllProfessionals", {
            cityName: selectedCity.name?.toLowerCase(),
            professionObjective: profession.objective,
            professionPlural: profession.plural,
          })
        }}
      </NuxtLink>
      <PeopleSwiperEmpty v-else>
        <i18n path="peopleSwiper.emptyProfession">
          <template #professionTaskName>
            <span class="font-bold">{{ profession.name }}</span>
          </template>
        </i18n>
      </PeopleSwiperEmpty>
    </div>
  </section>
</template>

<script>
import { inject } from "@nuxtjs/composition-api";
import { EVENTS } from "~/pages/professionals/_profession/events";
import PeopleSwiperEmpty from "~/components/swiper/PeopleSwiperEmpty.vue";
import PeopleSwiper from "~/components/swiper/PeopleSwiper.vue";
import CardPerson from "~/components/people/CardPerson.vue";
import AutocompleteCity from "~/components/_refactored/autocomplete/AutocompleteCity.vue";

export default {
  components: {
    PeopleSwiperEmpty,
    PeopleSwiper,
    CardPerson,
    AutocompleteCity,
  },
  setup() {
    return {
      profession: inject("profession"),
      peopleByCity: inject("peopleByCity"),
      loading: inject("loading"),
      selectedCity: inject("selectedCity"),
      cities: inject("cities"),
    };
  },
  computed: {
    firstFewCitiesWithPersonCount() {
      return this.cities?.slice(0, Math.min(this.cities?.length, 7)) ?? [];
    },
    selectedCityIndex() {
      return this.firstFewCitiesWithPersonCount?.findIndex(
        (city) => city.seoName === this.selectedCity.seoName
      );
    },
  },
  methods: {
    changeCity(city) {
      this.$mitt.emit(EVENTS.PROFESSIONS_PAGE_CHANGE_CITY, city);
    },
  },
};
</script>

<style lang="scss" scoped>
@use "styles/_all-members" as global;
$bodyBg: rgba(64, 79, 91, 0.05);

.contentHeader {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  @include global.touch {
    flex-direction: column;
  }

  .ptTabs {
    display: flex;
    align-items: center;
    user-select: none;
    max-width: calc(100% - 300px);
    overflow-x: auto;
    @include global.touch {
      max-width: unset;
      width: 100%;
    }

    .ptTab {
      padding: 15px 20px;
      font-weight: 600;
      font-size: 16px;
      cursor: pointer;
      white-space: nowrap;
      border-radius: 12px 12px 0px 0px;

      &::first-letter {
        text-transform: capitalize;
      }

      &.active {
        color: global.$primary;
        font-weight: 700;
        background: $bodyBg;
      }
    }
  }

  .cityButtons {
    display: flex;
    align-items: center;
    user-select: none;
    background: $bodyBg;
    border-radius: 12px 12px 0px 0px;
    padding: 15px 15px;
    padding-bottom: 10px;
    max-width: calc(100% - 300px);
    overflow-x: auto;
    @include global.touch {
      max-width: unset;
      width: 100%;
    }

    .cityButton {
      padding: 15px 20px;
      padding-bottom: 10px;
      font-weight: 600;
      font-size: 16px;
      cursor: pointer;
      white-space: nowrap;
      border-radius: 12px 12px 0px 0px;
      font-size: 14px;
      padding: 5px 15px;
      background: white;
      border-radius: 3px;
      margin-right: 10px;

      &:last-of-type {
        margin-right: 0px;
      }

      &::first-letter {
        text-transform: capitalize;
      }

      &.active {
        font-weight: 700;
        background: global.$primary;
        color: white;
      }
    }
  }

  .search {
    padding: 15px;
    padding-bottom: 0px;
    background: $bodyBg;
    border-radius: 12px 12px 0px 0px;
    @include global.touch {
      width: 100%;
      border-radius: 0px;
    }
  }
}

.contentBody {
  border-radius: 0px 0px 13px 13px;
  width: 100%;
  padding: 15px;
  position: relative;
  background: $bodyBg;

  p {
    font-weight: 600;
    font-size: 18px;
    line-height: 160%;
    color: global.$blogTextColor;
    margin-bottom: 1rem;
  }

  button {
    margin-bottom: 1rem;
  }

  h2 {
    font-weight: 700;
    font-size: 20px;
    line-height: 160%;
    color: global.$primary;
    margin-bottom: 5px;
  }
}
</style>
